// The grammar of an object key in the images bucket, and the one rule that says
// whether a caller is allowed to touch it (#5016).
//
// Kept pure and free of any crypto import so both sides can use the SAME parser.
// The server verifies keys (`server/functions/src/helpers/storage-object-owner.ts`);
// the client mints them. Each computes the owner token with its own sha256 and
// they share only the domain constant below plus everything in this file. Two
// parsers that disagreed about what an owned path looks like would let a caller
// write a key the server then refuses to delete, stranding the object forever
// with nothing reporting it.
//
// The client half lands in the commit after the one that introduced this file,
// so until it ships every key in the bucket classifies as `legacy` — which is
// the intended intermediate state, not a fault.

// Domain-separation prefix for the storage owner token. Deliberately NOT the
// same string as AUTHOR_IDENTITY_HASH_DOMAIN: a published exercise carries its
// author's identity hash in a document many people can read, so reusing that
// domain would let anybody holding such an exercise work out the author's image
// prefix and enumerate their uploads. The `:v1` suffix reserves a migration
// path — a `:v2` rotation can start minting new keys without breaking old ones,
// because an old key stays valid under whatever token it was minted with.
export const STORAGE_OWNER_HASH_DOMAIN = 'mh-physique:storage-object-owner:v1';

// The one top-level folder these endpoints may ever act on. The AI scanners'
// photo folders (label-scan-images/, meal-scan-images/, equipment-scan-images/,
// recipe-scan-images/, workout-plan-scan-images/) used to live in this bucket too,
// and now live in a private bucket the SERVER writes with its own credentials
// (#5938). They stay outside the grammar anyway, so no client can ever reach a
// scan photo left behind here or put there by mistake.
export const IMAGE_ROOT_SEGMENT = 'images';

// The marker segment that says "the next segment is an owner token". A legacy
// key's second segment is a collection name or a document id, so a two-letter
// marker plus a strict 64-hex token is what tells the two shapes apart.
export const OWNER_MARKER_SEGMENT = 'o';

const OWNER_TOKEN_PATTERN = /^[0-9a-f]{64}$/;

/** Where every object owned by `ownerToken` lives. Trailing slash included, so
 * callers can concatenate without inventing the separator themselves. */
export function buildOwnedImagePrefix(ownerToken: string): string {
  return `${IMAGE_ROOT_SEGMENT}/${OWNER_MARKER_SEGMENT}/${ownerToken}/`;
}

/** What a path is, structurally — before anyone asks who is calling. */
export interface ParsedStorageObjectPath {
  /** false when the path is not a shape any of our code produces. */
  valid: boolean;
  /** the owner token, when the path carries one. Absent on a legacy path. */
  ownerToken?: string;
}

/**
 * Parse an object key against the grammar above.
 *
 * Invalid covers more than malformed input, and every case here is deliberate:
 *
 * - anything outside `images/`, which keeps the scanner folders unreachable;
 * - a LEADING slash, which real objects in the bucket genuinely have — issue
 *   #3707 baked `<bucket>//images/...` into keys minted by old builds. Those
 *   objects are legacy by definition and nobody should be deleting them on a
 *   caller's say-so, so refusing the shape is the right answer, not a gap;
 * - an empty segment anywhere, and `.` or `..` segments. GCS treats a key as an
 *   opaque string and does no normalisation, so these are not traversal in the
 *   filesystem sense — but they let one object be named several ways, and an
 *   ownership check that can be written round is not a check;
 * - the owner marker with anything other than a 64-character lowercase hex
 *   token after it, so `images/o/<attacker's choice>/...` cannot be minted.
 */
export function parseStorageObjectPath(path: string): ParsedStorageObjectPath {
  if (!path || typeof path !== 'string') {
    return { valid: false };
  }
  const segments = path.split('/');
  // a leading slash produces an empty first segment; so does a doubled slash
  // anywhere. both are caught by the empty-segment check below.
  if (segments.length < 2) {
    return { valid: false };
  }
  for (const segment of segments) {
    if (segment === '' || segment === '.' || segment === '..') {
      return { valid: false };
    }
  }
  if (segments[0] !== IMAGE_ROOT_SEGMENT) {
    return { valid: false };
  }
  if (segments[1] !== OWNER_MARKER_SEGMENT) {
    // legacy: under images/, carries no owner. Valid shape, unknown owner.
    return { valid: true };
  }
  // owned shape: images/o/<token>/<at least one more segment>
  if (segments.length < 4) {
    return { valid: false };
  }
  const ownerToken = segments[2];
  if (!OWNER_TOKEN_PATTERN.test(ownerToken)) {
    return { valid: false };
  }
  return { valid: true, ownerToken };
}

/** What the caller is allowed to do with a path.
 *
 * `legacy` is not a failure — it is every object that existed before owner
 * tokens were minted, and there is no way to learn who owns one. The endpoints
 * decide what that means for them; it differs between reading and writing.
 */
export type StorageObjectAccess = 'own' | 'foreign' | 'legacy' | 'invalid';

/**
 * Decide whether `callerOwnerToken` may act on `path`.
 *
 * Pure, and takes the caller's TOKEN rather than their uid, so this file needs
 * no crypto and the same rule can be unit-tested without hashing anything.
 */
export function classifyStorageObjectPath(path: string, callerOwnerToken: string): StorageObjectAccess {
  const parsed = parseStorageObjectPath(path);
  if (!parsed.valid) {
    return 'invalid';
  }
  if (parsed.ownerToken === undefined) {
    return 'legacy';
  }
  // an empty or missing caller token must never match an owned path. it would
  // mean "we could not work out who is calling", and the answer to that is no.
  if (!callerOwnerToken) {
    return 'foreign';
  }
  return parsed.ownerToken === callerOwnerToken ? 'own' : 'foreign';
}
